import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { LLMTabContentForDispatch } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import type { PositionedItem } from '../CustomDash/components/PageConfigProvider/types/item';

/**
 * Consume-once stash for a PRE-GENERATED custom page layout (resolve-first
 * Phase C, meeting decision 2026-07-08).
 *
 * The `create_custom_page` tool now runs the page-gen stream HEADLESSLY
 * during the turn (drawer shows the streamed thinking) and the Build chip
 * carries the finished layout. On click, the drawer stashes the layout here
 * keyed by the freshly-built draft path and navigates; `NewCustomPage`
 * consumes the stash and seeds the draft config with the generated items —
 * so the user lands on a COMPLETE layout whose widgets simply data-load.
 * No empty draft, no on-page build stream, no resumed turn.
 *
 * Consume-once + TTL: a stale stash (user navigated elsewhere, or reloads
 * the draft path later) must never resurrect an old generation — the draft
 * then falls back to the normal empty-page flow.
 */

export type PendingGeneratedPage = {
  content: LLMTabContentForDispatch;
  /** Page-level filters the generation resolved (applied via command store). */
  filters?: TalentDiscoveryAiFilterSearchResponseV2;
  prompt: string;
  /**
   * Invoked by the consumer (`CustomDashRoute`) with the MINTED widget items
   * right after it seeds the draft config. Conversion mints fresh item uuids,
   * so the consumer is the only place the ids the widgets mount with exist —
   * the drawer uses them to arm the landing-narration collector (see
   * seeded-build-narration.ts). Fires at most once (consume-once stash);
   * never fires if the stash expires unconsumed.
   */
  onSeeded?: (seededItems: PositionedItem[]) => void;
  at: number;
};

const PENDING_TTL_MS = 60_000;

const pendingByPath = new Map<string, PendingGeneratedPage>();

/**
 * Key by PATHNAME only: the draft path from `createPageDraft` carries the
 * current search string ("/company?filters=…") while the consumer looks up by
 * `location.pathname` ("/company") — normalizing here keeps both callers
 * agreeing regardless of which form they hold.
 */
function toKey(path: string): string {
  const q = path.indexOf('?');
  return q === -1 ? path : path.slice(0, q);
}

export function stashGeneratedPage(
  path: string,
  payload: Omit<PendingGeneratedPage, 'at'>
): void {
  pendingByPath.set(toKey(path), { ...payload, at: Date.now() });
}

/** Returns and REMOVES the stash for `path` when present and fresh. */
export function takeGeneratedPage(
  path: string
): PendingGeneratedPage | undefined {
  const hit = pendingByPath.get(toKey(path));
  if (!hit) {
    return undefined;
  }
  pendingByPath.delete(toKey(path));
  if (Date.now() - hit.at > PENDING_TTL_MS) {
    return undefined;
  }
  return hit;
}

/** Test hook. */
export function __resetPendingGeneratedPagesForTests(): void {
  pendingByPath.clear();
}
